<template>
  <div class="rating-view">
    <h2 class="rating-title">Рейтинг Региональных Организаций</h2>

    <!-- Фильтры -->
    <div class="filter-controls">
      <label for="year-select">Год:</label>
      <select id="year-select" v-model="selectedYear" @change="updateFilters">
        <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
      </select>

      <label for="quarter-select">Квартал:</label>
      <select id="quarter-select" v-model="selectedQuarter" @change="updateFilters">
        <option value="1">1 квартал</option>
        <option value="2">2 квартал</option>
        <option value="3">3 квартал</option>
        <option value="4">4 квартал</option>
      </select>
    </div>

    <!-- Индикатор загрузки и ошибки -->
    <div v-if="isLoading" class="loading">Загрузка данных...</div>
    <div v-else-if="getError" class="error">Ошибка: {{ getError }}</div>

    <!-- Таблица -->
    <table v-else-if="ratingData.length > 0 && criteriaHeaders.length > 0" class="rating-table">
      <thead>
        <tr>
          <th rowspan="2">Место</th>
          <th rowspan="2">Субъект РФ</th>
          <th rowspan="2">Организация</th>
          <!-- Группировка заголовков -->
          <th
            v-for="(criterionIds, groupKey) in criteriaGroups"
            :key="groupKey"
            :colspan="criterionIds.length"
          >
            {{ getGroupName(groupKey) }}
          </th>
          <!-- Переместили "Итого" в конец первой строки -->
          <th rowspan="2">Итого за {{ selectedQuarter }} квартал {{ selectedYear }}</th>
          <th v-if="showYearlyTotals" rowspan="2">Итого за 1-{{ selectedQuarter }} кварталы {{ selectedYear }}</th>
        </tr>
        <tr>
          <!-- Заголовки критериев -->
          <th v-for="criterion in criteriaHeaders" :key="criterion.id">
            {{ criterion.name }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in ratingData" :key="row.region.id">
          <td>{{ row.place }}</td>
          <td>{{ row.region.name }}</td>
          <td>{{ row.region.organization_name || 'N/A' }}</td>
          <!-- Критерии -->
          <td v-for="criterion in criteriaHeaders" :key="criterion.id">
            <template v-if="isManualCriterion(criterion)">
              <input
                :value="getManualInputValue(row.region.id, criterion.id)"
                @input="setManualInputValue(row.region.id, criterion.id, $event.target.value)"
                type="number"
                step="0.1"
                min="0"
                :class="{ 'saving': isSaving(`${row.region.id}-${criterion.id}`) }"
                @blur="handleBlurSave(row, criterion, $event)"
                @keydown.enter="handleEnterSave(row, criterion, $event)"
                @focus="clearError(`${row.region.id}-${criterion.id}`)"
              />
              <div v-if="getInputError(`${row.region.id}-${criterion.id}`)" class="input-error">
                {{ getInputError(`${row.region.id}-${criterion.id}`) }}
              </div>
            </template>
            <template v-else>
              {{ getScoreForCriterion(row.criteria_scores, criterion.id) }}
            </template>
          </td>
          <!-- Итого за квартал -->
          <td>{{ row.total_points }}</td>
          <!-- Итого за год -->
          <td v-if="showYearlyTotals">{{ getYearlyTotal(row) }}</td>
        </tr>
      </tbody>
    </table>

    <div v-else-if="!isLoading && !getError" class="no-data">Нет данных для отображения.</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'RatingView',
  data() {
    return {
      yearOptions: [],
      manualInputValues: {}, // Локальное состояние для редактирования
      // Добавим новое поле для кэширования execution_id
      // Структура: { 'regionId-criterionId': executionId }
      executionIdCache: {},
    };
  },
  computed: {
    ...mapGetters('rating', [
      'getCriteria', 'getRatingData', 'getRatingDataYearly', 'getCriteriaGroups',
      'getSelectedQuarter', 'getSelectedYear', 'isLoading', 'getError',
      'getSavingCells', 'getInputErrors'
    ]),
    ratingData() {
      // console.log('[COMPONENT] ratingData computed:', this.getRatingData.length); // Для отладки
      return this.getRatingData;
    },
    ratingDataYearly() {
      return this.getRatingDataYearly;
    },
    criteriaHeaders() {
      // console.log('[COMPONENT] criteriaHeaders computed:', this.getCriteria.length); // Для отладки
      return this.getCriteria;
    },
    criteriaGroups() {
      return this.getCriteriaGroups;
    },
    selectedQuarter: {
      get() { return this.getSelectedQuarter; },
      set(value) { this.UPDATE_SELECTED_QUARTER(value); }
    },
    selectedYear: {
      get() { return this.getSelectedYear; },
      set(value) { this.UPDATE_SELECTED_YEAR(value); }
    },
    showYearlyTotals() {
      return this.selectedQuarter <= 3;
    }
  },
  created() {
    const currentYear = new Date().getFullYear();
    const startYear = 2025;
    this.yearOptions = Array.from({ length: Math.max(1, currentYear - startYear + 5) }, (_, i) => startYear + i);
  },
  async mounted() {
    // console.log('[COMPONENT] Mounted started'); // Для отладки
    await this.loadAllData();
    // console.log('[COMPONENT] Mounted finished'); // Для отладки
  },
  methods: {
    ...mapActions('rating', [
      'FETCH_CRITERIA', 'FETCH_RATING_DATA', 'UPDATE_SELECTED_QUARTER', 'UPDATE_SELECTED_YEAR', 'SAVE_MANUAL_SCORE'
    ]),

    async loadAllData() {
      // console.log('[COMPONENT] loadAllData started'); // Для отладки
      await this.FETCH_CRITERIA();
      // console.log('[COMPONENT] Criteria loaded'); // Для отладки
      await this.FETCH_RATING_DATA();
      // console.log('[COMPONENT] Rating data loaded'); // Для отладки
    },
    async updateFilters() {
      await this.FETCH_RATING_DATA();
    },

    isManualCriterion(criterion) {
      return criterion.calculation_type === 'manual';
    },

    getManualInputValue(regionId, criterionId) {
      const cellKey = `${regionId}-${criterionId}`;
      if (this.manualInputValues.hasOwnProperty(cellKey)) {
        return this.manualInputValues[cellKey];
      }
      const row = this.ratingData.find(r => r.region.id === regionId);
      if (row) {
        const scoreObj = row.criteria_scores.find(cs => cs.criterion.id === criterionId);
        // Проверим, есть ли уже закэшированный execution_id
        const cachedId = this.executionIdCache[cellKey];
        if (scoreObj) {
          // Если закэширован, но в данных его нет - используем кэш
          // Это может быть после первого POST
          if (cachedId && scoreObj.execution_id === undefined) {
            // Обновим локально scoreObj, чтобы он содержал execution_id
            // Это нужно только для определения existingId в saveCellValue
            // Не изменяем оригинальный объект Vuex напрямую!
            // Просто возвращаем значение и запомним, что execution_id есть
            return String(scoreObj.points_awarded);
          }
          // Если в данных есть execution_id (редкий случай, если бэкенд его возвращает)
          if (scoreObj.execution_id) {
             this.executionIdCache[cellKey] = scoreObj.execution_id;
          }
          return String(scoreObj.points_awarded);
        }
      }
      return '0';
    },

    // Метод для получения existingId
    getExistingIdForSave(regionId, criterionId) {
      const cellKey = `${regionId}-${criterionId}`;
      // Сначала проверим кэш
      if (this.executionIdCache[cellKey]) {
        return this.executionIdCache[cellKey];
      }
      // Затем проверим в данных (на случай, если бэкенд всё-таки начнёт возвращать execution_id)
      const row = this.ratingData.find(r => r.region.id === regionId);
      if (row) {
        const scoreObj = row.criteria_scores.find(cs => cs.criterion.id === criterionId);
        if (scoreObj && scoreObj.execution_id) {
          this.executionIdCache[cellKey] = scoreObj.execution_id;
          return scoreObj.execution_id;
        }
      }
      return null;
    },

    setManualInputValue(regionId, criterionId, value) {
      const cellKey = `${regionId}-${criterionId}`;
      this.$set(this.manualInputValues, cellKey, value);
    },

    getScoreForCriterion(criteria_scores, criterion_id) {
      const scoreObj = criteria_scores.find(item => item.criterion.id === criterion_id);
      return scoreObj ? scoreObj.points_awarded : 0;
    },

    getGroupName(groupKey) {
      const names = {
        'INFO': 'Информационное обеспечение',
        'PROJECT': 'Проектная и программная деятельность'
      };
      return names[groupKey] || groupKey;
    },

    getCellKey(regionId, criterionId) {
      return `${regionId}-${criterionId}`;
    },

    isSaving(cellKey) {
      return this.getSavingCells[cellKey];
    },

    getInputError(cellKey) {
      return this.getInputErrors[cellKey];
    },

    clearError(cellKey) {
      this.$store.commit('rating/SET_CELL_ERROR', { key: cellKey, error: null });
    },

    // Исправлено: Поиск existingId и вызов SAVE_MANUAL_SCORE
    async saveCellValue(row, criterion, value) {
      const cellKey = this.getCellKey(row.region.id, criterion.id);
      const points_awarded = value.trim() === '' ? 0 : parseFloat(value);

      // Используем метод для получения existingId
      const existingId = this.getExistingIdForSave(row.region.id, criterion.id);

      // Найдём текущую запись для evidence (если она есть)
      let evidence = 'Обновлено через интерфейс';
      if (row.criteria_scores) {
        const currentScore = row.criteria_scores.find(cs => cs.criterion.id === criterion.id);
        if (currentScore) {
          evidence = currentScore.evidence || evidence;
        }
      }

      try {
        const response = await this.SAVE_MANUAL_SCORE({
          regionId: row.region.id,
          criterionId: criterion.id,
          quarter: this.selectedQuarter,
          points_awarded: points_awarded,
          evidence: evidence,
          existingId: existingId // Передаём existingId в action
        });

        // После успешного POST или PATCH, обновим кэш
        // response.data.id - это id новой записи (при POST) или обновлённой (при PATCH)
        if (response.data && response.data.id) {
          this.executionIdCache[cellKey] = response.data.id;
        }

        this.$delete(this.manualInputValues, cellKey);
        console.log(`[COMPONENT] saveCellValue successful for ${cellKey}, executionId: ${this.executionIdCache[cellKey]}`); // Лог
      } catch (error) {
        // Ошибка уже установлена в store
        console.error(`[COMPONENT] saveCellValue failed for ${cellKey}:`, error);
      }
    },

    handleBlurSave(row, criterion, event) {
      this.setManualInputValue(row.region.id, criterion.id, event.target.value);
      this.saveCellValue(row, criterion, event.target.value);
    },

    handleEnterSave(row, criterion, event) {
      event.preventDefault();
      event.target.blur();
    },

    getYearlyTotal(row) {
      const yearlyRow = this.ratingDataYearly.find(r => r.region.id === row.region.id);
      return yearlyRow ? yearlyRow.total_points_yearly : 0;
    }
  },
};
</script>



<style scoped>

.rating-table input[type="number"] {
  width: 60px;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
}

.rating-table input[type="number"].saving {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.input-error {
  color: #f56c6c;
  font-size: 0.8em;
  margin-top: 2px;
}
/* Ваши стили */
.rating-view {
  padding: 20px;
}

.rating-title {
  margin-bottom: 20px;
  color: #303133;
}

.filter-controls {
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  align-items: center;
}

.filter-controls label {
  font-weight: bold;
  color: #606266;
}

.filter-controls select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  background-color: white;
}

.loading, .error, .no-data {
  padding: 10px;
  margin: 10px 0;
  border-radius: 4px;
  text-align: center;
}

.loading {
  background-color: #e0f7fa;
  color: #006064;
}

.error {
  background-color: #ffebee;
  color: #b71c1c;
}

.no-data {
  background-color: #f5f5f5;
  color: #666;
}

.rating-table {
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto; /* Добавлено для прокрутки при широкой таблице */
}

.rating-table, .rating-table th, .rating-table td {
  border: 1px solid #ddd;
}

.rating-table th, .rating-table td {
  padding: 12px;
  text-align: left;
}

.rating-table th {
  background-color: #f2f2f2;
  position: sticky; /* Фиксируем заголовки при прокрутке */
  top: 0;
}

.rating-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.rating-table tr:hover {
  background-color: #f5f5f5;
}
</style>